import { Add, Delete, Edit, Search, TrendingUp } from "@mui/icons-material";
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import { format, startOfMonth } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Printer3dIcon from '../../components/icons/Printer3dIcon';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import JobListSpecs from '../../components/jobs/JobListSpecs';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatDate, formatUuid } from '../../helpers/formatters';
import { useListFetch } from '../../hooks/useListFetch';
import { useReloadOnPageRestore } from '../../hooks/useReloadOnPageRestore';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import { filterActivePrinters, sortPrintersByRegistrationOrder } from '../../helpers/printers';
import { printersInProgressSet } from '../../helpers/printersOverview';
import {
    buildJobsListQuery,
    buildPrinterStatsQuery,
    buildPrinterSuccessCards,
    buildSharedJobsQueryParams,
    clampJobsDateRange,
    formatFilamentGrams,
    formatProductionDurationMinutes,
    getJobStatusBadgeProps,
    hasActiveJobsQueryFilters,
    jobsDateRangeForApi,
    JOB_STATUSES,
    maxJobsDateRangeEnd,
    normalizeJobsDateRange,
    PRINTER_STATS_PERIOD_MODES,
    resolveEffectiveStatsPeriodMode,
    resolvePrinterStatsPeriod,
    resolvePrinterStatsQueryParams,
    statusLabel,
    successRateFromTotals,
    summarizePrinterStatsProduction,
} from '../../helpers/jobs';
import {
    fetchJobDetails,
    mergeJobInList,
    patchPrinterStatsForNotification,
    patchPrintersInProgressSet,
} from '../../helpers/jobLivePatch';
import { seedJobLiveInProgress } from '../../helpers/jobLiveInProgress';
import { isJobIntegrationLiveNotification, subscribeLiveNotifications } from '../../helpers/liveNotifications';
import { liveUpdateHighlightSx, useLiveUpdateHighlights } from '../../helpers/liveUpdateHighlight';

const EMPTY_QUERY_FILTERS = {
    printer_uuid: '',
    status: '',
    finished_from: '',
    finished_to: '',
};

function initialJobFiltersFromSearchParams(searchParams) {
    return {
        printer_uuid: searchParams.get('printer_uuid') || '',
        status: searchParams.get('status') || '',
        finished_from: searchParams.get('finished_from') || '',
        finished_to: searchParams.get('finished_to') || '',
    };
}

function ListJobsPage() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const initialFilters = initialJobFiltersFromSearchParams(searchParams);
    const [jobs, setJobs] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [printersById, setPrintersById] = useState({});
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalJobs, setTotalJobs] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const { isPrinterHighlighted, isJobHighlighted } = useLiveUpdateHighlights(isJobIntegrationLiveNotification);
    const [draftPrinter, setDraftPrinter] = useState(initialFilters.printer_uuid);
    const [draftStatus, setDraftStatus] = useState(initialFilters.status);
    const [draftDateFrom, setDraftDateFrom] = useState(initialFilters.finished_from);
    const [draftDateTo, setDraftDateTo] = useState(initialFilters.finished_to);
    const [queryFilters, setQueryFilters] = useState(initialFilters);
    const [printerStats, setPrinterStats] = useState([]);
    const [printersInProgress, setPrintersInProgress] = useState(() => new Set());
    const defaultStatsPeriod = useMemo(() => {
        const today = new Date();
        return {
            from: format(startOfMonth(today), 'yyyy-MM-dd'),
            to: format(today, 'yyyy-MM-dd'),
        };
    }, []);
    const [statsPeriodMode, setStatsPeriodMode] = useState(
        initialFilters.finished_from || initialFilters.finished_to ? 'list_filters' : 'all_time',
    );
    const [statsCustomFrom, setStatsCustomFrom] = useState(defaultStatsPeriod.from);
    const [statsCustomTo, setStatsCustomTo] = useState(defaultStatsPeriod.to);
    const [statsLoading, setStatsLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasActiveFilters = hasActiveJobsQueryFilters(queryFilters);

    const sharedQueryParams = useMemo(
        () => buildSharedJobsQueryParams(queryFilters, { searchTerm, appliedSearchTerm }),
        [queryFilters, searchTerm, appliedSearchTerm],
    );

    const effectiveStatsPeriodMode = useMemo(
        () => resolveEffectiveStatsPeriodMode(statsPeriodMode, queryFilters),
        [statsPeriodMode, queryFilters],
    );

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                const list = sortPrintersByRegistrationOrder(response.data.printers || []);
                setPrinters(list);
                const byId = {};
                list.forEach((p) => {
                    byId[p.id] = { name: p.name, brand: p.brand };
                });
                setPrintersById(byId);
            })
            .catch(() => {});
    }, []);

    const fetchPrinterStats = useCallback(() => {
        const resolved = resolvePrinterStatsQueryParams({
            mode: effectiveStatsPeriodMode,
            queryFilters,
            customFrom: statsCustomFrom,
            customTo: statsCustomTo,
            searchTerm,
            appliedSearchTerm,
        });
        if (resolved.error) {
            Swal.fire('Período inválido', resolved.error, 'warning');
            return Promise.resolve();
        }

        const statsQuery = buildPrinterStatsQuery(resolved.params);

        setStatsLoading(true);
        return Promise.all([
            api.get(`jobs/printer-stats?${statsQuery}`),
            api.get('jobs?status=em_andamento&limit=200'),
        ])
            .then(([statsResponse, inProgressResponse]) => {
                const inProgressJobs = inProgressResponse.data.data?.jobs || [];
                setPrinterStats(statsResponse.data.data?.printers || []);
                seedJobLiveInProgress(inProgressJobs);
                setPrintersInProgress(printersInProgressSet(inProgressJobs));
            })
            .catch(() => {
                Swal.fire('Erro', 'Não foi possível carregar as taxas de sucesso.', 'error');
            })
            .finally(() => {
                setStatsLoading(false);
            });
    }, [effectiveStatsPeriodMode, queryFilters, statsCustomFrom, statsCustomTo, searchTerm, appliedSearchTerm]);

    const fetchJobs = useCallback(() => {
        setIsLoading(true);
        const listQuery = buildJobsListQuery({
            page: currentPage,
            limit: itemsPerPage,
            ...sharedQueryParams,
        });

        return api.get(`jobs?${listQuery}`)
            .then((jobsResponse) => {
                const data = jobsResponse.data.data || {};
                setJobs(data.jobs || []);
                setTotalPages(data.total_pages || 1);
                setTotalJobs(data.total ?? (data.jobs || []).length);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, sharedQueryParams]);

    const bumpListFetch = useCallback(() => {
        setListVersion((v) => v + 1);
    }, []);

    useReloadOnPageRestore(bumpListFetch);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }

            setPrintersInProgress((prev) => patchPrintersInProgressSet(prev, detail));
            setPrinterStats((prev) => patchPrinterStatsForNotification(prev, detail));

            fetchJobDetails(detail.jobUuid).then((job) => {
                if (!job) {
                    return;
                }

                setJobs((prev) => mergeJobInList(prev, job, {
                    filters: {
                        ...queryFilters,
                        searchTerm,
                        appliedSearchTerm,
                    },
                    currentPage,
                    pageSize: itemsPerPage,
                }));
            });
        });
    }, [queryFilters, searchTerm, appliedSearchTerm, currentPage, itemsPerPage]);

    useListFetch(fetchJobs, [
        location.key,
        currentPage,
        itemsPerPage,
        appliedSearchTerm,
        listVersion,
        fetchJobs,
    ]);

    useEffect(() => {
        if (statsPeriodMode !== 'custom') {
            fetchPrinterStats();
        }
    }, [effectiveStatsPeriodMode, queryFilters, sharedQueryParams, fetchPrinterStats]);

    const printerSuccessCards = useMemo(
        () => buildPrinterSuccessCards(filterActivePrinters(printers), printerStats, printersInProgress),
        [printers, printerStats, printersInProgress],
    );

    const productionSummary = useMemo(
        () => summarizePrinterStatsProduction(printerStats),
        [printerStats],
    );

    const productionSuccessRate = useMemo(
        () => successRateFromTotals(productionSummary.total_printed, productionSummary.total_objects),
        [productionSummary],
    );

    const triggerReload = () => {
        setCurrentPage(1);
        setListVersion((v) => v + 1);
    };

    const handleSearch = () => {
        setAppliedSearchTerm(true);
        triggerReload();
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setAppliedSearchTerm(false);
        triggerReload();
    };

    const handleDraftDateFromChange = (value) => {
        setDraftDateFrom(value);
        if (!value) {
            return;
        }
        const clamped = clampJobsDateRange(value, draftDateTo || value);
        setDraftDateFrom(clamped.start);
        setDraftDateTo(clamped.end);
    };

    const handleDraftDateToChange = (value) => {
        setDraftDateTo(value);
        if (!value) {
            return;
        }
        const clamped = clampJobsDateRange(draftDateFrom || value, value);
        setDraftDateFrom(clamped.start);
        setDraftDateTo(clamped.end);
    };

    const handleApplyFilters = () => {
        const range = jobsDateRangeForApi(draftDateFrom, draftDateTo);
        if (range.error) {
            Swal.fire('Período inválido', range.error, 'warning');
            return;
        }
        const clamped = clampJobsDateRange(draftDateFrom, draftDateTo);
        setDraftDateFrom(clamped.start);
        setDraftDateTo(clamped.end);
        setQueryFilters({
            printer_uuid: draftPrinter,
            status: draftStatus,
            finished_from: range.finished_from || '',
            finished_to: range.finished_to || '',
        });
        setStatsPeriodMode('list_filters');
        triggerReload();
    };

    const handleClearFilters = () => {
        setDraftPrinter('');
        setDraftStatus('');
        setDraftDateFrom('');
        setDraftDateTo('');
        setQueryFilters(EMPTY_QUERY_FILTERS);
        setStatsPeriodMode('all_time');
        triggerReload();
    };

    const handleStatsPeriodModeChange = (_event, value) => {
        if (value) {
            setStatsPeriodMode(value);
        }
    };

    const handleStatsCustomDateFromChange = (value) => {
        setStatsCustomFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(value, statsCustomTo || value);
        setStatsCustomFrom(normalized.start);
        setStatsCustomTo(normalized.end);
    };

    const handleStatsCustomDateToChange = (value) => {
        setStatsCustomTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(statsCustomFrom || value, value);
        setStatsCustomFrom(normalized.start);
        setStatsCustomTo(normalized.end);
    };

    const handleApplyStatsPeriod = () => {
        fetchPrinterStats();
    };

    const statsListFiltersHint = useMemo(() => {
        if (effectiveStatsPeriodMode !== 'list_filters') {
            return '';
        }
        const parts = [];
        if (queryFilters.printer_uuid) {
            const printer = printers.find((p) => p.id === queryFilters.printer_uuid);
            parts.push(`Impressora: ${printer?.name || 'selecionada'}`);
        }
        if (queryFilters.status) {
            parts.push(`Status: ${statusLabel(queryFilters.status)}`);
        }
        if (queryFilters.finished_from && queryFilters.finished_to) {
            parts.push(`Período: ${queryFilters.finished_from} a ${queryFilters.finished_to}`);
        } else if (!queryFilters.printer_uuid && !queryFilters.status) {
            parts.push('Sem filtros aplicados na lista (todas as impressões)');
        }
        return parts.join(' · ');
    }, [effectiveStatsPeriodMode, queryFilters, printers]);

    const draftDateToMax = draftDateFrom ? maxJobsDateRangeEnd(draftDateFrom) : '';
    const draftDateToMin = draftDateFrom || '';
    const draftDateFromMax = draftDateTo || '';

    const handleCreateJob = () => {
        navigate('/admin/register-job');
    };

    const handleEditJob = (id) => {
        navigate(`/admin/edit-job/${id}`);
    };

    const handleDeleteJob = (jobId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta impressão será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                api.delete(`jobs/${jobId}`)
                    .then(() => {
                        setJobs((prev) => prev.filter((job) => job.id !== jobId));
                        Swal.fire('Removido!', 'A impressão foi removida com sucesso.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover a impressão.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Impressões</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                Impressões
            </Typography>

            {printers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mb: 1.5,
                        }}
                    >
                        <Typography variant="subtitle1">
                            Taxa de sucesso por impressora
                            {hasActiveFilters && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    ({totalJobs} impressões no período)
                                </Typography>
                            )}
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            color="primary"
                            exclusive
                            value={effectiveStatsPeriodMode}
                            onChange={handleStatsPeriodModeChange}
                            sx={{ flexWrap: 'wrap' }}
                        >
                            {PRINTER_STATS_PERIOD_MODES.map((option) => (
                                <ToggleButton
                                    key={option.value}
                                    value={option.value}
                                    disabled={hasActiveFilters && option.value !== 'list_filters'}
                                >
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {hasActiveFilters && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            Os cards usam os mesmos filtros da lista abaixo.
                        </Typography>
                    )}

                    {effectiveStatsPeriodMode === 'list_filters' && statsListFiltersHint && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                            {statsListFiltersHint}
                        </Typography>
                    )}

                    {statsPeriodMode === 'custom' && !hasActiveFilters && (
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Data inicial"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={statsCustomFrom}
                                    onChange={(e) => handleStatsCustomDateFromChange(e.target.value)}
                                    inputProps={{ max: statsCustomTo || undefined }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Data final"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={statsCustomTo}
                                    onChange={(e) => handleStatsCustomDateToChange(e.target.value)}
                                    inputProps={{ min: statsCustomFrom || undefined }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button variant="contained" onClick={handleApplyStatsPeriod} disabled={statsLoading}>
                                    Atualizar período
                                </Button>
                            </Grid>
                        </Grid>
                    )}

                    {statsLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!statsLoading && printerStats.length > 0 && (
                        <Card variant="outlined" sx={{ mb: 2, opacity: statsLoading ? 0.5 : 1 }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Tempo (estimado)
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {formatProductionDurationMinutes(productionSummary.total_duration_minutes)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Filamento (estimado)
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {formatFilamentGrams(productionSummary.total_filament_grams)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Sucesso em objetos
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {productionSuccessRate.toFixed(1).replace('.', ',')}%
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Impressões no período
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {productionSummary.total_jobs}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    <Grid container spacing={2} sx={{ opacity: statsLoading ? 0.5 : 1 }}>
                        {printerSuccessCards.map((printerCard) => {
                            const brandBadge = getPrinterBrandBadgeProps(printerCard.brand);
                            const successRate = Number(printerCard.success_rate) || 0;
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={printerCard.printer_uuid}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            borderColor: printerCard.is_printing ? 'success.main' : 'divider',
                                            boxShadow: printerCard.is_printing
                                                ? '0 0 0 1px rgba(46, 125, 50, 0.35)'
                                                : 'none',
                                            ...liveUpdateHighlightSx(isPrinterHighlighted(printerCard.printer_uuid)),
                                        }}
                                    >
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    gap: 1,
                                                    mb: 1.5,
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0 }}>
                                                    {brandBadge ? (
                                                        <Chip
                                                            label={printerCard.name}
                                                            size="small"
                                                            {...brandBadge}
                                                        />
                                                    ) : (
                                                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                            {printerCard.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {printerCard.is_printing && (
                                                    <Chip
                                                        size="small"
                                                        icon={<Printer3dIcon fontSize="small" />}
                                                        label="Imprimindo"
                                                        color="success"
                                                        variant="filled"
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                                <TrendingUp fontSize="small" color="primary" />
                                                <Typography variant="body2" color="text.secondary">
                                                    Taxa de sucesso
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                                                {successRate.toFixed(1).replace('.', ',')}%
                                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                    ({printerCard.total_jobs} impressões)
                                                </Typography>
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(100, successRate)}
                                                sx={{ height: 6, borderRadius: 1, mb: 1 }}
                                            />
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {printerCard.total_printed}/{printerCard.total_objects} objetos impressos
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                {formatProductionDurationMinutes(printerCard.total_duration_minutes)}
                                                {' · '}
                                                {formatFilamentGrams(printerCard.total_filament_grams)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    mb: 2,
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto',
                        flex: 1,
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar impressão..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateJob}
                    startIcon={<Add />}
                    fullWidth={isMobile}
                    sx={{ flexShrink: 0 }}
                >
                    Impressão
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Filtros
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-printer-label">Impressora</InputLabel>
                            <Select
                                labelId="filter-printer-label"
                                label="Impressora"
                                value={draftPrinter}
                                onChange={(e) => setDraftPrinter(e.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {printers.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-status-label">Status</InputLabel>
                            <Select
                                labelId="filter-status-label"
                                label="Status"
                                value={draftStatus}
                                onChange={(e) => setDraftStatus(e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {JOB_STATUSES.map((s) => (
                                    <MenuItem key={s.value} value={s.value}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateFrom}
                            onChange={(e) => handleDraftDateFromChange(e.target.value)}
                            inputProps={{ max: draftDateFromMax || undefined }}
                            helperText="Início do período (máx. 1 mês)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateTo}
                            onChange={(e) => handleDraftDateToChange(e.target.value)}
                            inputProps={{
                                min: draftDateToMin || undefined,
                                max: draftDateToMax || undefined,
                            }}
                            disabled={!draftDateFrom}
                            helperText={draftDateFrom ? 'Até 1 mês após a data inicial' : 'Informe a data inicial'}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        Filtrar
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Paper>

            {!isLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {totalJobs} impressão{totalJobs === 1 ? '' : 'ões'} encontrada{totalJobs === 1 ? '' : 's'}
                </Typography>
            )}

            {!isLoading && jobs.length === 0 && (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">Nenhuma impressão encontrada.</Typography>
                </Paper>
            )}

            <Stack spacing={1.25}>
                {Array.isArray(jobs) && jobs.length > 0 && jobs.map((job) => {
                    const printer = printersById[job.printer_uuid];
                    const printerLabel = printer?.name || formatUuid(job.printer_uuid);
                    const brandBadge = getPrinterBrandBadgeProps(printer?.brand);
                    const successRate = job.success_percent != null ? `${job.success_percent}%` : '—';
                    const highlighted = isJobHighlighted(job.id) || isJobHighlighted(job.uuid);

                    return (
                        <Paper
                            key={job.id}
                            variant="outlined"
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                borderColor: highlighted ? 'primary.main' : 'divider',
                                boxShadow: highlighted ? '0 0 0 1px rgba(25, 118, 210, 0.35)' : 'none',
                                '&:hover': {
                                    borderColor: 'primary.light',
                                    boxShadow: 1,
                                },
                            }}
                        >
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={5}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={job.file_name}
                                    >
                                        {job.file_name}
                                    </Typography>
                                    <JobListSpecs job={job} />
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, lineHeight: 1.6 }}>
                                        Início: {formatDate(job.started_at)}
                                        {' · '}
                                        Fim: {formatDate(job.finished_at)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6} sm={4} md={2}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        Impressora
                                    </Typography>
                                    {brandBadge ? (
                                        <Chip label={printerLabel} size="small" {...brandBadge} />
                                    ) : (
                                        <Typography variant="body2" fontWeight={600} noWrap title={printerLabel}>
                                            {printerLabel}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6} sm={4} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        Status
                                    </Typography>
                                    <Chip {...getJobStatusBadgeProps(job.status)} />
                                    {job.status_reason && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ mt: 0.75, lineHeight: 1.45 }}
                                        >
                                            {job.status_reason}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Objetos: <strong>{job.printed_count}/{job.object_count}</strong>
                                        {' · '}
                                        Sucesso: <strong>{successRate}</strong>
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    md={2}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                        gap: 0.5,
                                    }}
                                >
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditJob(job.id)}
                                        aria-label="editar impressão"
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteJob(job.id)}
                                        aria-label="remover impressão"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}
            </Stack>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                        setListVersion((v) => v + 1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                        setListVersion((v) => v + 1);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListJobsPage;
